import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  BarChart3, 
  ExternalLink,
  Briefcase,
  FileText,
  Award,
  Map,
  ShoppingBag,
  Clock,
  Layout,
  Globe
} from 'lucide-react';
import { getContentAnalytics } from '../../../lib/siteAnalyticsService';
import { databaseService } from '../../../lib/appwrite';

export default function AllContentAnalytics() {
  const [loading, setLoading] = useState(true);
  const [allContent, setAllContent] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [timeFilter, setTimeFilter] = useState('monthly'); // daily, monthly, total
  const [sortBy, setSortBy] = useState('views'); // views, visitors, date
  const [contentTitles, setContentTitles] = useState({}); // Cache for real titles

  const contentTypes = [
    { value: 'all', label: 'All', icon: Layout },
    { value: 'projects', label: 'Projects', icon: Briefcase },
    { value: 'blogs', label: 'Blogs', icon: FileText },
    { value: 'certificates', label: 'Certificates', icon: Award },
    { value: 'maps', label: 'Maps', icon: Map },
    { value: 'products', label: 'Shop', icon: ShoppingBag }
  ];

  const getDaysFromFilter = (filter) => {
    switch(filter) {
      case 'daily': return 1;
      case 'monthly': return 30;
      case 'total': return 3650;
      default: return 30;
    }
  };

  useEffect(() => {
    loadAllContent();
  }, [timeFilter]);

  // Fetch real titles when content changes
  useEffect(() => {
    if (allContent.length > 0) {
      fetchRealTitles();
    }
  }, [allContent]);

  const loadAllContent = async () => {
    setLoading(true);
    try {
      const days = getDaysFromFilter(timeFilter);
      const types = ['projects', 'blogs', 'certificates', 'maps', 'products'];
      const promises = types.map(type => getContentAnalytics(type, days));
      const results = await Promise.all(promises);
      
      let aggregated = [];
      results.forEach((res, index) => {
        if (res.success) {
          const type = types[index];
          const items = res.data.map(item => ({ ...item, type }));
          aggregated = [...aggregated, ...items];
        }
      });
      
      setAllContent(aggregated);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealTitles = async () => {
    const newTitles = { ...contentTitles };
    let hasUpdates = false;

    // Filter items that don't have a cached title yet
    const itemsToFetch = allContent.filter(item => !newTitles[item.contentId] && item.contentId !== 'homepage');

    // Fetch in parallel
    await Promise.all(itemsToFetch.map(async (item) => {
      try {
        let collectionName = item.type;
        // Map 'maps' type to actual collection name 'spatial_maps'
        if (collectionName === 'maps') collectionName = 'spatial_maps';
        
        const result = await databaseService.getDocument(collectionName, item.contentId);
        if (result.success) {
          const realTitle = result.data.title || result.data.name || result.data.header || 'Untitled';
          newTitles[item.contentId] = realTitle;
          hasUpdates = true;
        }
      } catch (e) {
        // Silent fail
      }
    }));

    if (hasUpdates) {
      setContentTitles(newTitles);
    }
  };

  const filteredContent = useMemo(() => {
    return allContent
      .filter(item => {
        const matchesType = selectedType === 'all' || item.type === selectedType;
        const realTitle = contentTitles[item.contentId] || item.title || item.contentSlug || '';
        const matchesSearch = realTitle.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesType && matchesSearch;
      })
      .sort((a, b) => {
        if (sortBy === 'views') return b.views - a.views;
        if (sortBy === 'visitors') return b.uniqueVisitors - a.uniqueVisitors;
        return 0; 
      });
  }, [allContent, selectedType, searchQuery, sortBy, contentTitles]);

  const getTypeIcon = (type) => {
    switch(type) {
      case 'projects': return <Briefcase className="w-4 h-4 text-orange-500" />;
      case 'blogs': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'certificates': return <Award className="w-4 h-4 text-yellow-500" />;
      case 'maps': return <Map className="w-4 h-4 text-green-500" />;
      case 'products': return <ShoppingBag className="w-4 h-4 text-purple-500" />;
      default: return <BarChart3 className="w-4 h-4 text-gray-500" />;
    }
  };

  const getPublicLink = (type, slug) => {
    switch(type) {
        case 'projects': return `/project/${slug}`;
        case 'blogs': return `/blog/${slug}`;
        case 'certificates': return `/certificate/${slug}`;
        case 'maps': return `/map/${slug}`;
        case 'products': return `/product/${slug}`;
        default: return '#';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4C1D95]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Header & Controls - Compressed to one line */}
      <div className="flex flex-col lg:flex-row gap-4 items-center bg-white p-2 rounded-xl shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
        
        {/* Left: Back & Title */}
        <div className="flex items-center gap-3 pr-4 border-r border-gray-100 flex-shrink-0">
          <Link to="/admin/analytics" className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <span className="font-bold text-gray-900 text-lg whitespace-nowrap">All Content</span>
        </div>

        {/* Center: Tabs & Time Filter */}
        <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 flex-grow overflow-hidden">
          {/* Type Tabs */}
          <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-lg overflow-x-auto no-scrollbar flex-shrink">
            {contentTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-all ${
                  selectedType === type.value
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <type.icon className={`w-3.5 h-3.5 ${selectedType === type.value ? 'text-[#4C1D95]' : ''}`} />
                {type.label}
              </button>
            ))}
          </div>

          {/* Time Filter - Ultra Compact */}
          <div className="flex bg-gray-50 p-0.5 rounded-lg flex-shrink-0">
            {['daily', 'monthly', 'total'].map((type) => (
              <button
                key={type}
                onClick={() => setTimeFilter(type)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-md capitalize transition-all ${
                  timeFilter === type 
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Search & Sort - Optimized Space */}
        <div className="flex items-center gap-2 flex-shrink-0">
           {/* Expanding Search Icon */}
           <div className="relative group">
             <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
               <Search className="w-4 h-4 text-gray-500" />
             </div>
             <input 
               type="text" 
               value={searchQuery}
               onChange={(e) => setSearchQuery(e.target.value)}
               className="pl-9 pr-3 py-1.5 bg-transparent hover:bg-gray-50 focus:bg-gray-50 border border-transparent focus:border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4C1D95] w-10 focus:w-48 transition-all duration-300 placeholder-transparent focus:placeholder-gray-400 cursor-pointer focus:cursor-text"
               placeholder="Search..."
             />
           </div>
           
           {/* Sort - Compact */}
           <div className="relative">
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-2 pr-7 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4C1D95] cursor-pointer"
              >
                 <option value="views">Views</option>
                 <option value="visitors">Visitors</option>
              </select>
              <BarChart3 className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
           </div>
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Content</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider font-right text-right">Type</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Views</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Visitors</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredContent.length > 0 ? (
              filteredContent.map((item, index) => {
                // Use cached title if available, otherwise fallback
                const displayTitle = contentTitles[item.contentId] || item.title || item.contentSlug || 'Untitled Content';
                
                return (
                  <tr key={`${item.type}-${item.contentId}`} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="font-medium text-gray-900 group-hover:text-[#4C1D95] transition-colors line-clamp-1">
                           {displayTitle}
                        </div>
                        {index < 3 && sortBy === 'views' && (
                           <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-gray-100 text-gray-700' :
                              'bg-orange-100 text-orange-700'
                           }`}>
                             #{index + 1}
                           </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                         <Clock className="w-3 h-3" />
                         Last viewed recently
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 border border-gray-100">
                         {getTypeIcon(item.type)}
                         <span className="text-xs font-medium text-gray-600 capitalize">{item.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="font-bold text-gray-900">{item.views?.toLocaleString()}</div>
                    </td>
                     <td className="px-6 py-4 text-right">
                      <div className="font-medium text-gray-600">{item.uniqueVisitors?.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                       <div className="flex items-center justify-end gap-2">
                         <Link 
                           to={`/admin/analytics/${item.type}/${item.contentId}`}
                           className="p-2 text-gray-400 hover:text-[#4C1D95] hover:bg-[#4C1D95]/5 rounded-lg transition-colors"
                           title="View Analytics"
                         >
                            <BarChart3 className="w-4 h-4" />
                         </Link>
                         {item.customSlug && (
                            <a 
                              href={getPublicLink(item.type, item.customSlug)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Open Link"
                            >
                               <ExternalLink className="w-4 h-4" />
                            </a>
                         )}
                       </div>
                    </td>
                  </tr>
                );
              })
            ) : (
               <tr>
                 <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                       <Search className="w-8 h-8 mb-2 opacity-50" />
                       <p>No content found matching your filters</p>
                    </div>
                 </td>
               </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

